/**
 * Authentication Context for MedInterpret
 *
 * Provides user authentication state and methods throughout the application.
 * Uses MSAL for Azure AD Single Sign-On.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { AccountInfo, InteractionStatus } from '@azure/msal-browser';
import { loginRequest, graphConfig, AUDITOR_ROLE_VALUE } from './msalConfig';
import { setRefreshTokenFunction } from '../services/api';

export interface AuditorUser {
  id: string; // Azure AD Object ID (oid)
  name: string;
  email: string;
  photoUrl?: string;
  roles: string[];
  isAuditor: boolean;
}

interface AuthContextType {
  user: AuditorUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isRefreshing: boolean;
  error: Error | null;
  login: () => Promise<void>;
  logout: () => void;
  getAccessToken: () => Promise<string | null>;
  refreshToken: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState<AuditorUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | undefined>(undefined);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const account = accounts[0] || null;

  // Ref to track refresh promise for deduplication
  const refreshPromiseRef = React.useRef<Promise<boolean> | null>(null);

  /**
   * Fetch user photo from Microsoft Graph API
   */
  const fetchUserPhoto = useCallback(async (accessToken: string): Promise<string | undefined> => {
    try {
      const response = await fetch(graphConfig.graphPhotoEndpoint, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        return URL.createObjectURL(blob);
      }
      return undefined;
    } catch (err) {
      console.warn('Failed to fetch user photo:', err);
      return undefined;
    }
  }, []);

  /**
   * Extract user information from account and ID token claims
   */
  const extractUserFromAccount = useCallback(
    (acc: AccountInfo, photo?: string): AuditorUser => {
      const idTokenClaims = acc.idTokenClaims as Record<string, any> | undefined;
      const roles = (idTokenClaims?.roles as string[]) || [];

      return {
        id: acc.localAccountId, // This is the oid claim
        name: acc.name || idTokenClaims?.name || 'Unknown',
        email: acc.username || idTokenClaims?.email || idTokenClaims?.preferred_username || '',
        photoUrl: photo,
        roles,
        isAuditor: roles.includes(AUDITOR_ROLE_VALUE),
      };
    },
    []
  );

  /**
   * Get access token for API calls
   */
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!account) return null;

    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account,
        forceRefresh: false,
      });
      return response.accessToken;
    } catch (err: any) {
      // Handle block_iframe_reload, timed_out, and other silent acquisition errors
      if (err.errorCode === 'block_iframe_reload' ||
          err.errorCode === 'timed_out' ||
          err.name === 'InteractionRequiredAuthError' ||
          err.errorCode === 'interaction_required') {
        console.warn('Silent token acquisition failed, user interaction may be required:', err.errorCode || err.name);
        // Don't automatically redirect - let the calling code decide
        return null;
      }
      console.error('Failed to acquire token silently:', err);
      return null;
    }
  }, [instance, account]);

  /**
   * Refresh token with exponential backoff
   * Attempts 3 times with delays of 1s, 2s, 4s
   * On definitive failure, redirects to /login
   */
  const refreshToken = useCallback(async (): Promise<boolean> => {
    // If refresh already in progress, return existing promise
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    const doRefresh = async (): Promise<boolean> => {
      if (!account) {
        window.location.href = '/login';
        return false;
      }

      setIsRefreshing(true);
      const backoffDelays = [1000, 2000, 4000];

      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const response = await instance.acquireTokenSilent({
            ...loginRequest,
            account,
            forceRefresh: true,
          });

          if (response.accessToken) {
            setIsRefreshing(false);
            return true;
          }
        } catch (err: any) {
          console.warn(`Token refresh attempt ${attempt + 1} failed:`, err.errorCode || err.name);

          // If interaction is required or iframe timed out, redirect immediately
          if (err.name === 'InteractionRequiredAuthError' ||
              err.errorCode === 'interaction_required' ||
              err.errorCode === 'login_required' ||
              err.errorCode === 'timed_out' ||
              err.errorCode === 'block_iframe_reload') {
            setIsRefreshing(false);
            window.location.href = '/login';
            return false;
          }

          // Wait before retry (except on last attempt)
          if (attempt < 2) {
            await new Promise(resolve => setTimeout(resolve, backoffDelays[attempt]));
          }
        }
      }

      // All attempts failed - redirect to login
      setIsRefreshing(false);
      window.location.href = '/login';
      return false;
    };

    refreshPromiseRef.current = doRefresh();

    try {
      return await refreshPromiseRef.current;
    } finally {
      refreshPromiseRef.current = null;
    }
  }, [instance, account]);

  /**
   * Register refreshToken function with API module
   */
  useEffect(() => {
    setRefreshTokenFunction(refreshToken);
  }, [refreshToken]);

  /**
   * Initialize user data when account changes
   */
  useEffect(() => {
    // Wait for MSAL to finish any in-progress operations
    if (inProgress !== InteractionStatus.None) {
      return;
    }

    const initializeUser = async () => {
      if (!account) {
        setUser(null);
        setPhotoUrl(undefined);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        // Get access token for photo
        const accessToken = await getAccessToken();
        let photo: string | undefined;

        if (accessToken) {
          photo = await fetchUserPhoto(accessToken);
          setPhotoUrl(photo);
        }

        const extractedUser = extractUserFromAccount(account, photo);
        setUser(extractedUser);
      } catch (err) {
        console.error('Failed to initialize user:', err);
        setError(err instanceof Error ? err : new Error('Failed to initialize user'));
      } finally {
        setIsLoading(false);
      }
    };

    initializeUser();
  }, [account, inProgress, getAccessToken, fetchUserPhoto, extractUserFromAccount]);

  /**
   * Login via redirect (more reliable than popup)
   */
  const login = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);
      // Use redirect instead of popup for better compatibility
      await instance.loginRedirect(loginRequest);
    } catch (err) {
      console.error('Login failed:', err);
      setError(err instanceof Error ? err : new Error('Login failed'));
      throw err;
    }
    // Note: No finally block - redirect will navigate away
  }, [instance]);

  /**
   * Logout and clear session
   */
  const logout = useCallback((): void => {
    // Revoke photo URL to prevent memory leaks
    if (photoUrl) {
      URL.revokeObjectURL(photoUrl);
    }

    instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
  }, [instance, photoUrl]);

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading: isLoading || inProgress !== InteractionStatus.None,
    isRefreshing,
    error,
    login,
    logout,
    getAccessToken,
    refreshToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to access authentication context
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
